import type { RootState } from '@/store';
import { GraphService, GraphType } from '@/modules/ui';
import { captureThrowOnDev } from '@/util';
import { useAppSelector } from '@/hooks';

export const selectUploadProgress = (state: RootState) =>
  state.ui.app.uploadProgress;

export const selectAppBarStyle = (state: RootState) =>
  state.ui.app.appBar.style;

export const selectImageViewerState = (state: RootState) =>
  state.ui.app.imageViewer;

export const selectNewPatientSnackbar = (state: RootState) =>
  state.ui.app.newPatientSnackbar;

export const selectLastOpenedPatient = (state: RootState) =>
  state.ui.app.lastOpenedPatient;

export const selectGraphType = (state: RootState) =>
  state.ui.app.graph.graphType;

export const selectGraphDataType = (state: RootState) => {
  const graphState = state.ui.app.graph;
  if (GraphService.isPredefined(graphState.graphType)) {
    return GraphService.getDataType(graphState.graphType);
  }

  if (graphState.graphType === GraphType.CUSTOM) {
    const t = graphState.customGraph?.dataType;
    if (!t) {
      captureThrowOnDev(
        'Illegal state: Graph type is CUSTOM but no data type was present'
      );
    }
    return t;
  }

  return undefined;
};

export const selectGraphDataFields =
  (snake = false, includeTimestamp = true) =>
  (state: RootState): string[] => {
    const graphState = state.ui.app.graph;
    let fields: string[] = [];
    if (GraphService.isPredefined(graphState.graphType)) {
      fields = GraphService.getDataFieldsForGraph(graphState.graphType, snake);
    }

    if (graphState.graphType === GraphType.CUSTOM) {
      const customGraphFields = graphState.customGraph?.fields;
      if (!customGraphFields) {
        captureThrowOnDev(
          'Illegal state: Graph type is CUSTOM but no data fields were present'
        );
      } else {
        fields = customGraphFields.map((f) => f.path);
      }
    }

    if (includeTimestamp) {
      return ['timestamp', ...fields];
    }
    return fields;
  };

export const selectGraphPeriod = (state: RootState) => ({
  dateFrom: state.ui.app.graph.dateFrom,
  dateTo: state.ui.app.graph.dateTo
});

export const selectGraphBuilderState = (state: RootState) =>
  state.ui.app.graphBuilder;

export const selectWearableDataType = (state: RootState) =>
  state.ui.app.wearableDataState.dataType;
export const selectWearableDataPeriod = (state: RootState) => ({
  dateFrom: state.ui.app.wearableDataState.dateFrom,
  dateTo: state.ui.app.wearableDataState.dateTo,
  latestOnly: state.ui.app.wearableDataState.latestOnly
});

const selectTabFocused = (state: RootState) =>
  state.ui.app.tabState.visibility === 'visible' && state.ui.app.tabState.focus;

export const useIsTabFocused = () => useAppSelector(selectTabFocused);
